import React, { useState } from "react";
import { useSubAssetLinkedViewbyID } from "../stressEntry/stressEntryQueries";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import MuiTable from "@material-ui/core/Table";
import { formatDate } from "../../../common/formatDate";
import UpdateIcon from "@material-ui/icons/Update";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import CloseIcon from "@material-ui/icons/Close";
import FileDownloadIcon from "@material-ui/icons/GetApp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { toast } from "react-toastify";
import { TravelerModal } from "../../../common/travelerModal";
import { CalibrationHistoryDialog } from "./CalibrationHistoryDialog";
import { VerifyCalibrationDialog } from "./VerifyCalibrationDialog";
import { UpdateCalibrationDialog } from "./UpdateCalibrationDialog";
import { getTheme } from "../../../common/getLocalTheme";

export const AssetHistoryView = ({ assetID, inUse }: any) => {
  const [showRunDetails, setShowRunDetails] = useState(false);
  const [showCurrentRunDetails, setShowCurrentRunDetails] = useState(false);
  const [showPreviousRunDetails, setShowPreviousRunDetails] = useState(false);
  const [showMoreMap, setShowMoreMap] = useState<Record<string, boolean>>({});

  const [viewCalibrationDialogOpen, setViewCalibrationDialogOpen] =
    useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);

  const [open, setOpen] = React.useState(false);
  const [VerifyCalibration, setVerifyCalibration] = React.useState(false);

  // Download State
  const [downloadingRun, setDownloadingRun] = useState<string | null>(null);

  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // PDF Viewer State
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [viewingRun, setViewingRun] = useState<string | null>(null);
  const [currentViewFiles, setCurrentViewFiles] = useState<string[]>([]);
  const [currentViewRunKey, setCurrentViewRunKey] = useState<string>("");

  // File List Selection Dialog State
  const [fileListOpen, setFileListOpen] = useState(false);
  const [activeFilesList, setActiveFilesList] = useState<string[]>([]);
  const [activeFileTitle, setActiveFileTitle] = useState("");
  const [activeRunData, setActiveRunData] = useState<any>(null);
  const [activeViewingFile, setActiveViewingFile] = useState<any>(null);

  const addTime = (dateString: any, duration: number) => {
    if (!dateString || !duration) return "N/A";
    const oldDate = new Date(dateString);
    const newDate = new Date(oldDate.getTime() + duration * 60000);
    return formatDate(newDate.toString());
  };

  const {
    data: assets = [],
    isLoading: isLoadingAssets,
    isError: isErrorAsset,
    refetch,
  } = useSubAssetLinkedViewbyID(assetID);

  const handleOpenFileList = (
    files: any[] | string[],
    title: string,
    runData: any,
  ) => {
    if (!files || files.length === 0) {
      toast.error("No Files Available.");
      return;
    }
    setActiveFilesList(files);
    setActiveFileTitle(title);
    setActiveRunData(runData);
    setFileListOpen(true);
  };

  // Function to view the file in the dialog
  const handleViewFile = async (runFileObj: any, runKey: string) => {
    try {
      setActiveViewingFile(runFileObj); // Save reference for header download

      const fileUrl =
        typeof runFileObj === "string"
          ? runFileObj
          : runFileObj?.stress_download_url;
      if (!fileUrl) {
        toast.error("No file download URL available.");
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found.");
        return;
      }

      setViewingRun(runKey);
      setPdfViewerOpen(true);
      setIsPdfLoading(true);

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: { Authorization: `Token ${token}` },
      });

      if (!response.ok) {
        throw new Error(
          `Fetch failed: ${response.status} ${response.statusText}`,
        );
      }

      const contentType =
        response.headers.get("content-type") || "application/pdf";
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: contentType }),
      );

      setPdfBlobUrl(blobUrl);
    } catch (error) {
      console.error("View error:", error);
      toast.error("Failed to load file.");
      setPdfViewerOpen(false);
    } finally {
      setIsPdfLoading(false);
      setViewingRun(null);
    }
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
    }
    setPdfBlobUrl(null);
    setCurrentViewFiles([]);
    setCurrentViewRunKey("");
  };

  const handleDownload = async (
    runFileObj: any,
    runKey: string,
  ): Promise<void> => {
    try {
      const fileUrl =
        typeof runFileObj === "string"
          ? runFileObj
          : runFileObj?.stress_download_url;
      const fileName =
        typeof runFileObj === "object" ? runFileObj?.filename : "download.pdf";

      if (!fileUrl) {
        toast.error("No file available for download.");
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found.");
        return;
      }

      setDownloadingRun(runKey);

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: { Authorization: `Token ${token}` },
      });

      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`,
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName || `run-file-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);
      toast.success("File downloaded successfully.");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file.");
    } finally {
      setDownloadingRun(null);
    }
  };

  if (isLoadingAssets) return <LinearProgress />;

  return (
    <>
      <Grid>
        {assets.map((asset: any) => {
          const runs = asset?.stressrun_results ?? [];
          const hasRuns = runs.length > 0;

          return (
            <Paper key={asset.id} style={{ padding: 20, marginTop: 10 }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs>
                  <Grid style={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="subtitle1"
                      style={{ color: "#800280", fontSize: 16 }}
                    >
                      {asset?.asset_name || "N/A"}
                    </Typography>

                    {asset?.in_calibration ? (
                      <Box
                        style={{
                          backgroundColor: "#EFFDF4",
                          padding: "3px 8px",
                          border: "1px solid #B9F8CF",
                          borderRadius: "100px",
                          fontSize: "13px",
                          display: "flex",
                          alignItems: "center",
                          color: "#008000",
                          marginLeft: "5px",
                        }}
                      >
                        <img
                          src="/Icon_Available_Status.svg"
                          alt="status icon"
                          style={{ width: 14, height: 14, marginRight: 4 }}
                        />
                        {"Calibrated"}
                      </Box>
                    ) : (
                      <Box
                        style={{
                          backgroundColor: "#FEF3F2",
                          padding: "3px 8px",
                          border: "1px solid #FFCACA",
                          borderRadius: 100,
                          fontSize: 13,
                          display: "flex",
                          alignItems: "center",
                          marginLeft: 6,
                          color: "#FF0000",
                        }}
                      >
                        <PlayCircleOutlineIcon
                          style={{ fontSize: 14, marginRight: 4 }}
                        />
                        {"Calibrated required"}
                      </Box>
                    )}
                  </Grid>

                  <Typography
                    variant="body2"
                    style={{ color: "#0000008A", fontSize: 12 }}
                  >
                    {asset?.linkedsubassets || 0} linked sub-assets · Linked on{" "}
                    {asset?.linked_date ? formatDate(asset.linked_date) : "N/A"}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={() => {
                      setOpen(true);
                    }}
                  >
                    View Calibration History
                  </Button>
                </Grid>
                {asset?.in_calibration ? (
                  <Grid item>
                    {asset?.is_updated ? (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => {
                          setVerifyCalibration(true);
                        }}
                      >
                        Verify Calibration
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => {
                          setSelectedAsset(asset);
                          setViewCalibrationDialogOpen(true);
                        }}
                      >
                        Update Calibration
                      </Button>
                    )}
                  </Grid>
                ) : (
                  <Grid item>
                    {asset?.is_updated ? (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => {
                          setVerifyCalibration(true);
                        }}
                      >
                        Verify Calibration
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => {
                          setSelectedAsset(asset);
                          setViewCalibrationDialogOpen(true);
                        }}
                      >
                        Update Calibration
                      </Button>
                    )}
                  </Grid>
                )}
              </Grid>

              <Paper style={{ margin: 10, padding: 10 }} elevation={2}>
                {asset?.subassets?.length > 0 ? (
                  asset.subassets.map((asset: any, index: number) => (
                    <Paper
                      key={index}
                      style={{ margin: "10px 0", padding: "10px" }}
                      elevation={1}
                    >
                      {asset ? asset : "N/A"}
                    </Paper>
                  ))
                ) : (
                  <Paper
                    style={{ margin: "10px 0", padding: "10px" }}
                    elevation={1}
                  >
                    There's no assets linked to this asset.
                  </Paper>
                )}

                {inUse && !hasRuns ? (
                  <Paper
                    style={{
                      margin: "10px",
                      padding: "10px",
                    }}
                    elevation={1}
                  >
                    {asset?.reason || "No run details available."}
                  </Paper>
                ) : inUse ? (
                  <>
                    {/* IN USE: SHOW ALL RUNS WITH EXPAND/COLLAPSE */}
                    <Button
                      variant="contained"
                      color="primary"
                      style={{
                        padding: "10px 20px",
                        margin: "10px 0",
                      }}
                      endIcon={<UpdateIcon />}
                      onClick={() => {
                        setShowCurrentRunDetails((prev) => !prev);
                        setShowPreviousRunDetails(false);
                      }}
                    >
                      {showCurrentRunDetails
                        ? "Hide Current Run Details"
                        : "View Current Run Details"}
                    </Button>
                    {/* PREVIOUS RUN */}
                    <Button
                      variant="contained"
                      color="primary"
                      style={{
                        padding: "10px 20px",
                        margin: "10px 0 10px 10px",
                      }}
                      endIcon={<UpdateIcon />}
                      onClick={() => {
                        setShowPreviousRunDetails((prev) => !prev);
                        setShowCurrentRunDetails(false);
                      }}
                    >
                      {showPreviousRunDetails
                        ? "Hide Previous Run Details"
                        : "View Previous Run Details"}
                    </Button>

                    {showCurrentRunDetails && (
                      <Paper style={{ marginTop: 15, padding: 10 }}>
                        {(asset?.stressrun_results ?? []).map(
                          (run: any, runIndex: number) => {
                            const runKey = `current-${asset.id}-${runIndex}`;
                            const showMore = showMoreMap[runKey] ?? false;
                            const units = run?.units ?? [];
                            const unitsToShow = showMore
                              ? units
                              : units.slice(0, 6);

                            return (
                              <Paper
                                key={runKey}
                                style={{ margin: "10px 0", padding: 15 }}
                                elevation={2}
                              >
                                <Grid container alignItems="center" spacing={2}>
                                  <Grid item xs={12} container>
                                    <Grid item xs={6}>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <img
                                          src="/Icon_Current_Run.svg"
                                          alt="Current Run icon"
                                          style={{
                                            width: 30,
                                            height: 30,
                                            marginRight: 8,
                                          }}
                                        />
                                        <div>
                                          <Typography
                                            variant="body2"
                                            style={{ fontWeight: 700 }}
                                          >
                                            {run?.run_name || "N/A"}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            style={{
                                              color: "#0000008A",
                                              fontSize: 12,
                                            }}
                                          >
                                            Procedure:{" "}
                                            {run?.procedure_definition || "N/A"}
                                          </Typography>
                                        </div>
                                      </div>
                                    </Grid>
                                    {/* UPDATED BUTTON FOR VIEWING */}
                                    <Grid item xs={3}>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={
                                          getTheme() ||
                                          viewingRun === runKey ||
                                          downloadingRun === runKey
                                        }
                                        onClick={() =>
                                          handleOpenFileList(
                                            run?.run_files ?? [],
                                            `Stress Run Files - ${run?.run_name || "Run"}`,
                                            run,
                                          )
                                        }
                                        startIcon={
                                          viewingRun === runKey ||
                                          downloadingRun === runKey ? (
                                            <CircularProgress
                                              size={18}
                                              color="inherit"
                                            />
                                          ) : (
                                            <VisibilityIcon />
                                          )
                                        }
                                      >
                                        {viewingRun === runKey
                                          ? "Opening..."
                                          : downloadingRun === runKey
                                            ? "Downloading..."
                                            : "View Stress run file"}
                                      </Button>
                                    </Grid>

                                    <Grid item xs={3}>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={
                                          getTheme() ||
                                          viewingRun === runKey ||
                                          downloadingRun === runKey
                                        }
                                        onClick={() =>
                                          handleOpenFileList(
                                            run?.log_run_files ??
                                              run?.log_files ??
                                              [],
                                            `Stress Log Files - ${run?.run_name || "Run"}`,
                                            run,
                                          )
                                        }
                                        startIcon={
                                          viewingRun === `${runKey}-log` ? (
                                            <CircularProgress
                                              size={18}
                                              color="inherit"
                                            />
                                          ) : (
                                            <VisibilityIcon />
                                          )
                                        }
                                      >
                                        {viewingRun === `${runKey}-log`
                                          ? "Opening..."
                                          : "View Stress Log File"}
                                      </Button>
                                    </Grid>
                                  </Grid>

                                  {/* TIMELINE */}
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <AccessTimeIcon
                                        style={{
                                          marginRight: 6,
                                          color: "#0000008A",
                                        }}
                                      />
                                      Timeline
                                    </Typography>
                                    <Paper
                                      style={{ padding: 10, marginBottom: 10 }}
                                    >
                                      START DATE:{" "}
                                      {run?.start_date
                                        ? formatDate(run.start_date)
                                        : "N/A"}
                                    </Paper>
                                    <Paper
                                      style={{ padding: 10, marginBottom: 10 }}
                                    >
                                      EST END DATE:{" "}
                                      {addTime(run?.start_date, run?.duration)}
                                    </Paper>
                                  </Grid>

                                  {/* UNITS */}
                                  <Grid item xs={6}>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                    >
                                      Units
                                    </Typography>
                                    <Grid container spacing={1}>
                                      {unitsToShow.map(
                                        (unit: any, index: number) => (
                                          <Grid item key={index}>
                                            <TravelerModal
                                              serialNumber={unit}
                                            />
                                          </Grid>
                                        ),
                                      )}
                                      {units.length > 6 && (
                                        <Grid item xs={12}>
                                          <Button
                                            color="primary"
                                            style={{
                                              marginTop: 6,
                                              fontSize: 13,
                                              textTransform: "none",
                                            }}
                                            onClick={() =>
                                              setShowMoreMap((prev) => ({
                                                ...prev,
                                                [runKey]: !prev[runKey],
                                              }))
                                            }
                                          >
                                            {showMore
                                              ? "- Show Less"
                                              : `+ Show ${units.length - 6} More`}
                                          </Button>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Paper>
                            );
                          },
                        )}
                      </Paper>
                    )}

                    {/* PREVIOUS RUN DETAILS */}
                    {showPreviousRunDetails && (
                      <>
                        {(asset?.previousstressrun_results ?? []).length > 0 ? (
                          <Paper style={{ marginTop: 15, padding: 10 }}>
                            {(asset?.previousstressrun_results ?? []).map(
                              (run: any, runIndex: number) => {
                                const runKey = `previous-${asset.id}-${runIndex}`;
                                const showMore = showMoreMap[runKey] ?? false;
                                const units = run?.units ?? [];
                                const unitsToShow = showMore
                                  ? units
                                  : units.slice(0, 6);

                                return (
                                  <Paper
                                    key={runKey}
                                    style={{ margin: "10px 0", padding: 15 }}
                                    elevation={2}
                                  >
                                    <Grid
                                      container
                                      alignItems="center"
                                      spacing={2}
                                    >
                                      <Grid item xs={12} container>
                                        <Grid item xs={6}>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <img
                                              src="/Icon_Run_History.svg"
                                              alt="Previous Run icon"
                                              style={{
                                                width: 30,
                                                height: 30,
                                                marginRight: 8,
                                              }}
                                            />
                                            <div>
                                              <Typography
                                                variant="body2"
                                                style={{ fontWeight: 700 }}
                                              >
                                                {run?.run_name || "N/A"}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                style={{
                                                  color: "#0000008A",
                                                  fontSize: 12,
                                                }}
                                              >
                                                Procedure:{" "}
                                                {run?.procedure_definition ||
                                                  "N/A"}
                                              </Typography>
                                            </div>
                                          </div>
                                        </Grid>

                                        {/* UPDATED BUTTON FOR VIEWING */}
                                        <Grid item xs={3}>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={
                                              getTheme() ||
                                              viewingRun === runKey ||
                                              downloadingRun === runKey
                                            }
                                            onClick={() =>
                                              handleOpenFileList(
                                                run?.run_files ?? [],
                                                `Stress Run Files - ${run?.run_name || "Run"}`,
                                                run,
                                              )
                                            }
                                            startIcon={
                                              viewingRun === runKey ||
                                              downloadingRun === runKey ? (
                                                <CircularProgress
                                                  size={18}
                                                  color="inherit"
                                                />
                                              ) : (
                                                <VisibilityIcon />
                                              )
                                            }
                                          >
                                            {viewingRun === runKey
                                              ? "Opening..."
                                              : downloadingRun === runKey
                                                ? "Downloading..."
                                                : "View stress run file"}
                                          </Button>
                                        </Grid>

                                        <Grid item xs={3}>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={
                                              getTheme() ||
                                              viewingRun === runKey ||
                                              downloadingRun === runKey
                                            }
                                            onClick={() =>
                                              handleOpenFileList(
                                                run?.log_run_files ??
                                                  run?.log_files ??
                                                  [],
                                                `Stress Log Files - ${run?.run_name || "Run"}`,
                                                run,
                                              )
                                            }
                                            startIcon={
                                              viewingRun === `${runKey}-log` ? (
                                                <CircularProgress
                                                  size={18}
                                                  color="inherit"
                                                />
                                              ) : (
                                                <VisibilityIcon />
                                              )
                                            }
                                          >
                                            {viewingRun === `${runKey}-log`
                                              ? "Opening..."
                                              : "View Stress Log File"}
                                          </Button>
                                        </Grid>
                                      </Grid>

                                      <Grid item xs={6}>
                                        <Typography
                                          variant="subtitle1"
                                          gutterBottom
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <AccessTimeIcon
                                            style={{
                                              marginRight: 6,
                                              color: "#0000008A",
                                            }}
                                          />
                                          Timeline
                                        </Typography>
                                        <Paper
                                          style={{
                                            padding: 10,
                                            marginBottom: 10,
                                          }}
                                        >
                                          START DATE:{" "}
                                          {run?.start_date
                                            ? formatDate(run.start_date)
                                            : "N/A"}
                                        </Paper>
                                        <Paper
                                          style={{
                                            padding: 10,
                                            marginBottom: 10,
                                          }}
                                        >
                                          EST END DATE:{" "}
                                          {run?.start_date
                                            ? addTime(
                                                run.start_date,
                                                run.duration,
                                              )
                                            : "N/A"}
                                        </Paper>
                                      </Grid>

                                      <Grid item xs={6}>
                                        <Typography
                                          variant="subtitle1"
                                          gutterBottom
                                        >
                                          Units
                                        </Typography>
                                        <Grid container spacing={1}>
                                          {unitsToShow.map(
                                            (unit: any, index: number) => (
                                              <Grid item key={index}>
                                                <TravelerModal
                                                  serialNumber={unit}
                                                />
                                              </Grid>
                                            ),
                                          )}
                                          {units.length > 6 && (
                                            <Grid item xs={12}>
                                              <Button
                                                color="primary"
                                                style={{
                                                  marginTop: 6,
                                                  fontSize: 13,
                                                  textTransform: "none",
                                                }}
                                                onClick={() =>
                                                  setShowMoreMap((prev) => ({
                                                    ...prev,
                                                    [runKey]: !prev[runKey],
                                                  }))
                                                }
                                              >
                                                {showMore
                                                  ? "- Show Less"
                                                  : `+ Show ${units.length - 6} More`}
                                              </Button>
                                            </Grid>
                                          )}
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                );
                              },
                            )}
                          </Paper>
                        ) : (
                          <Paper
                            style={{
                              marginTop: 15,
                              padding: 20,
                              textAlign: "center",
                            }}
                            elevation={1}
                          >
                            <Typography
                              variant="body2"
                              style={{ color: "#777" }}
                            >
                              No previous run details available.
                            </Typography>
                          </Paper>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ padding: "10px 20px", margin: "10px 0" }}
                      endIcon={<UpdateIcon />}
                      onClick={() => {
                        setShowPreviousRunDetails((prev) => !prev);
                        setShowCurrentRunDetails(false);
                      }}
                    >
                      {showPreviousRunDetails
                        ? "Hide Last Run Details"
                        : "View Last Run Details"}
                    </Button>

                    {showPreviousRunDetails && (
                      <>
                        {(asset?.previousstressrun_results ?? []).length > 0 ? (
                          <Paper style={{ marginTop: 15, padding: 10 }}>
                            {(asset?.previousstressrun_results ?? []).map(
                              (run: any, runIndex: number) => {
                                const runKey = `previous-${asset.id}-${runIndex}`;
                                const showMore = showMoreMap[runKey] ?? false;
                                const units = run?.units ?? [];
                                const unitsToShow = showMore
                                  ? units
                                  : units.slice(0, 6);

                                return (
                                  <Paper
                                    key={runKey}
                                    style={{ margin: "10px 0", padding: 15 }}
                                    elevation={2}
                                  >
                                    <Grid
                                      container
                                      alignItems="center"
                                      spacing={2}
                                    >
                                      <Grid item xs={12} container>
                                        <Grid item xs={6}>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <img
                                              src="/Icon_Run_History.svg"
                                              alt="Previous Run icon"
                                              style={{
                                                width: 30,
                                                height: 30,
                                                marginRight: 8,
                                              }}
                                            />
                                            <div>
                                              <Typography
                                                variant="body2"
                                                style={{ fontWeight: 700 }}
                                              >
                                                {run?.run_name || "N/A"}
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                style={{
                                                  color: "#0000008A",
                                                  fontSize: 12,
                                                }}
                                              >
                                                Procedure:{" "}
                                                {run?.procedure_definition ||
                                                  "N/A"}
                                              </Typography>
                                            </div>
                                          </div>
                                        </Grid>

                                        {/* UPDATED BUTTON FOR VIEWING */}
                                        <Grid item xs={3}>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={
                                              getTheme() ||
                                              viewingRun === runKey ||
                                              downloadingRun === runKey
                                            }
                                            onClick={() =>
                                              handleOpenFileList(
                                                run?.run_files ?? [],
                                                `Stress Run Files - ${run?.run_name || "Run"}`,
                                                run,
                                              )
                                            }
                                            startIcon={
                                              viewingRun === runKey ||
                                              downloadingRun === runKey ? (
                                                <CircularProgress
                                                  size={18}
                                                  color="inherit"
                                                />
                                              ) : (
                                                <VisibilityIcon />
                                              )
                                            }
                                          >
                                            {viewingRun === runKey
                                              ? "Opening..."
                                              : downloadingRun === runKey
                                                ? "Downloading..."
                                                : "View stress run file"}
                                          </Button>
                                        </Grid>

                                        <Grid item xs={3}>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={
                                              getTheme() ||
                                              viewingRun === runKey ||
                                              downloadingRun === runKey
                                            }
                                            onClick={() =>
                                              handleOpenFileList(
                                                run?.log_run_files ??
                                                  run?.log_files ??
                                                  [],
                                                `Stress Log Files - ${run?.run_name || "Run"}`,
                                                run,
                                              )
                                            }
                                            startIcon={
                                              viewingRun === `${runKey}-log` ? (
                                                <CircularProgress
                                                  size={18}
                                                  color="inherit"
                                                />
                                              ) : (
                                                <VisibilityIcon />
                                              )
                                            }
                                          >
                                            {viewingRun === `${runKey}-log`
                                              ? "Opening..."
                                              : "View Stress Log File"}
                                          </Button>
                                        </Grid>
                                      </Grid>

                                      <Grid item xs={6}>
                                        <Typography
                                          variant="subtitle1"
                                          gutterBottom
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <AccessTimeIcon
                                            style={{
                                              marginRight: 6,
                                              color: "#0000008A",
                                            }}
                                          />
                                          Timeline
                                        </Typography>
                                        <Paper
                                          style={{
                                            padding: 10,
                                            marginBottom: 10,
                                          }}
                                        >
                                          START DATE:{" "}
                                          {run?.start_date
                                            ? formatDate(run.start_date)
                                            : "N/A"}
                                        </Paper>
                                        <Paper
                                          style={{
                                            padding: 10,
                                            marginBottom: 10,
                                          }}
                                        >
                                          EST END DATE:{" "}
                                          {run?.start_date
                                            ? addTime(
                                                run.start_date,
                                                run.duration,
                                              )
                                            : "N/A"}
                                        </Paper>
                                      </Grid>

                                      <Grid item xs={6}>
                                        <Typography
                                          variant="subtitle1"
                                          gutterBottom
                                        >
                                          Units
                                        </Typography>
                                        <Grid container spacing={1}>
                                          {unitsToShow.map(
                                            (unit: any, index: number) => (
                                              <Grid item key={index}>
                                                <TravelerModal
                                                  serialNumber={unit}
                                                />
                                              </Grid>
                                            ),
                                          )}
                                          {units.length > 6 && (
                                            <Grid item xs={12}>
                                              <Button
                                                color="primary"
                                                style={{
                                                  marginTop: 6,
                                                  fontSize: 13,
                                                  textTransform: "none",
                                                }}
                                                onClick={() =>
                                                  setShowMoreMap((prev) => ({
                                                    ...prev,
                                                    [runKey]: !prev[runKey],
                                                  }))
                                                }
                                              >
                                                {showMore
                                                  ? "- Show Less"
                                                  : `+ Show ${units.length - 6} More`}
                                              </Button>
                                            </Grid>
                                          )}
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                );
                              },
                            )}
                          </Paper>
                        ) : (
                          <Paper
                            style={{
                              marginTop: 15,
                              padding: 20,
                              textAlign: "center",
                            }}
                            elevation={1}
                          >
                            <Typography
                              variant="body2"
                              style={{ color: "#777" }}
                            >
                              {asset?.reason ||
                                "No previous run details available."}
                            </Typography>
                          </Paper>
                        )}
                      </>
                    )}
                  </>
                )}
              </Paper>
            </Paper>
          );
        })}
      </Grid>

      {/* FILE LIST DIALOG AS A TABLE */}
      <Dialog
        open={fileListOpen}
        onClose={() => setFileListOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{activeFileTitle}</Typography>
            <IconButton onClick={() => setFileListOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {activeFilesList.length > 0 ? (
            <MuiTable style={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: "bold" }}>Filename</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>Run-Name</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Upload Date
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Uploaded By
                  </TableCell>
                  <TableCell align="center" style={{ fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeFilesList.map((fileItem: any, index: number) => {
                  // Handle both object-based API responses and plain string fallback
                  const isObj =
                    typeof fileItem === "object" && fileItem !== null;
                  const filename = isObj
                    ? fileItem.filename
                    : `File #${index + 1}`;
                  // Access run_name (handling potential trailing space in keys)
                  const runName = isObj
                    ? fileItem["run_name "] ||
                      fileItem.run_name ||
                      activeRunData?.procedure_definition ||
                      "N/A"
                    : activeRunData?.procedure_definition || "N/A";

                  const uploadDate =
                    isObj && fileItem.run_date
                      ? formatDate(fileItem.run_date)
                      : activeRunData?.start_date
                        ? formatDate(activeRunData.start_date)
                        : "N/A";

                  const uploader =
                    isObj && fileItem.user
                      ? fileItem.user
                      : activeRunData?.username || "System";
                  const fileKey = `file-${index}`;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 600 }}
                        >
                          {filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{runName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{uploadDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{uploader}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          style={{ gap: 4 }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              setFileListOpen(false);
                              handleViewFile(fileItem, `file-${index}`);
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ padding: 20 }}
            >
              No files found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF VIEWER DIALOG WITH DOWNLOAD BUTTON */}
      <Dialog
        open={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: { height: "90vh", display: "flex", flexDirection: "column" },
        }}
      >
        <DialogTitle disableTypography style={{ padding: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding="16px 24px"
          >
            <Typography variant="h6">Run File Viewer</Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {/* Download Button inside PDF Viewer Header */}
              {activeViewingFile && (
                <IconButton
                  color="primary"
                  title="Download File"
                  disabled={downloadingRun === "pdf-viewer"}
                  onClick={() =>
                    handleDownload(activeViewingFile, "pdf-viewer")
                  }
                >
                  <FileDownloadIcon />
                </IconButton>
              )}
              <IconButton edge="end" onClick={handleClosePdfViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ padding: 0, flexGrow: 1 }}>
          {isPdfLoading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <CircularProgress />
              <Typography style={{ marginTop: 16, color: "#555" }}>
                Preparing your document. This might take a moment...
              </Typography>
            </Box>
          ) : pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="PDF Viewer"
            />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Typography color="error">No document loaded.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {viewCalibrationDialogOpen && (
        <UpdateCalibrationDialog
          open={viewCalibrationDialogOpen}
          onClose={() => setViewCalibrationDialogOpen(false)}
          asset={selectedAsset}
          onUpdated={refetch}
        />
      )}

      {open && (
        <CalibrationHistoryDialog
          open={open}
          onClose={() => setOpen(false)}
          onUpdated={refetch}
          assetID={assetID}
        />
      )}

      {VerifyCalibration && (
        <VerifyCalibrationDialog
          open={VerifyCalibration}
          onClose={() => setVerifyCalibration(false)}
          onUpdated={refetch}
          assetID={assetID}
        />
      )}
    </>
  );
};
